import { useState, useEffect } from 'react';
import { HitApi } from '../../Api/ApiHit';
import { searchIncentive, calculateDailyIncentive } from '../../constant/Constant';
import { getSelectedUnit, getElevateUser } from '../../storage/Storage';
import toast from 'react-hot-toast';
import PageHeader from '../../PageHeader/PageHeader';
import { Users, DollarSign, TrendingUp, FileText, CheckCircle, XCircle, Calendar, Calculator, Settings, X } from 'lucide-react';
import IncentiveSetting from './IncentiveSetting';

function IncentiveMaster() {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [dateRangeMode, setDateRangeMode] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Utility function to convert date string to timestamp
  const convertDateToTimestamp = (dateString, isStartOfDay = true) => {
    const [year, month, day] = dateString.split('-').map(Number);

    if (isStartOfDay) {
      // Start of day (00:00:00)
      return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
    } else {
      // End of day (23:59:59.999)
      return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
    }
  };

  // Get the actual number of selected days based on user input
  const getSelectedDaysCount = () => {
    if (!dateRangeMode || !startDate || !endDate) return 1;
    return Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
  };

  // Function to aggregate multiple days dashboard data into one
  const aggregateMultipleDaysData = (dashboardDocs) => {
    if (!dashboardDocs || dashboardDocs.length === 0) return null;
    if (dashboardDocs.length === 1) return dashboardDocs[0];

    // Use the first dashboard as base structure
    const baseDashboard = { ...dashboardDocs[0] };

    // Calculate number of days based on user selection, not data count
    const numberOfDays = getSelectedDaysCount();

    // Aggregate totals
    baseDashboard.totalIncentiveAmount = dashboardDocs.reduce((sum, doc) => sum + (doc.totalIncentiveAmount || 0), 0);
    baseDashboard.billCount = dashboardDocs.reduce((sum, doc) => sum + (doc.billCount || 0), 0);
    baseDashboard.serviceCount = dashboardDocs.reduce((sum, doc) => sum + (doc.serviceCount || 0), 0);
    baseDashboard.staffWithTargetAchieved = dashboardDocs.reduce((sum, doc) => sum + (doc.staffWithTargetAchieved || 0), 0);
    baseDashboard.staffWithoutTargetAchieved = dashboardDocs.reduce((sum, doc) => sum + (doc.staffWithoutTargetAchieved || 0), 0);

    // Aggregate staff members data
    const staffMap = new Map();
    dashboardDocs.forEach(doc => {
      doc.staffMembers?.forEach(staff => {
        const staffId = staff.staffId._id;
        if (staffMap.has(staffId)) {
          const existingStaff = staffMap.get(staffId);
          existingStaff.totalServiceValue += staff.totalServiceValue || 0;
          existingStaff.totalIncentiveAmount += staff.totalIncentiveAmount || 0;
          existingStaff.serviceCount += staff.serviceCount || 0;
          // Adjust daily target for multiple days (dailyTarget × selectedDays)
          existingStaff.adjustedDailyTarget = (existingStaff.dailyTarget || 0) * numberOfDays;
          // Calculate target percentage based on total service value vs adjusted target
          existingStaff.targetPercentage = Math.round((existingStaff.totalServiceValue / existingStaff.adjustedDailyTarget) * 100);
          // Recalculate target achievement based on adjusted target
          existingStaff.targetAchieved = existingStaff.totalServiceValue >= existingStaff.adjustedDailyTarget;
          // Recalculate incentive eligibility based on new target achievement
          existingStaff.incentiveEligible = existingStaff.targetAchieved;
        } else {
          const adjustedDailyTarget = (staff.dailyTarget || 0) * numberOfDays;
          const totalServiceValue = staff.totalServiceValue || 0;
          const newTargetAchieved = totalServiceValue >= adjustedDailyTarget;

          staffMap.set(staffId, {
            ...staff,
            totalServiceValue: totalServiceValue,
            totalIncentiveAmount: staff.totalIncentiveAmount || 0,
            serviceCount: staff.serviceCount || 0,
            targetAchieved: newTargetAchieved,
            incentiveEligible: newTargetAchieved,
            adjustedDailyTarget: adjustedDailyTarget,
            targetPercentage: Math.round((totalServiceValue / adjustedDailyTarget) * 100)
          });
        }
      });
    });

    baseDashboard.staffMembers = Array.from(staffMap.values());
    baseDashboard.staffCount = baseDashboard.staffMembers.length;

    // Recalculate staff target achievement counts based on new eligibility
    baseDashboard.staffWithTargetAchieved = baseDashboard.staffMembers.filter(staff => staff.targetAchieved).length;
    baseDashboard.staffWithoutTargetAchieved = baseDashboard.staffMembers.filter(staff => !staff.targetAchieved).length;

    // Combine all bills
    baseDashboard.bills = [];
    dashboardDocs.forEach(doc => {
      if (doc.bills && doc.bills.length > 0) {
        baseDashboard.bills.push(...doc.bills);
      }
    });

    // Sort bills by timestamp (newest first)
    baseDashboard.bills.sort((a, b) => b.timestamp - a.timestamp);

    // Update business date to range format
    const startDate = Math.min(...dashboardDocs.map(doc => doc.businessDate));
    const endDate = Math.max(...dashboardDocs.map(doc => doc.businessDate));
    baseDashboard.businessDate = startDate;
    baseDashboard.businessDateRange = { start: startDate, end: endDate };
    baseDashboard.numberOfDays = numberOfDays;

    return baseDashboard;
  };

  // Fetch Dashboard Data
  const fetchDashboard = async () => {
    setIsLoading(true);

    try {
      const searchParams = {
        unitId: getSelectedUnit()?._id
      };

      // Add date filter based on current mode
      if (dateRangeMode) {
        // Range mode - use start and end dates as timestamps
        const startTimestamp = convertDateToTimestamp(startDate, true); // Start of day
        const endTimestamp = convertDateToTimestamp(endDate, false); // End of day

        searchParams.businessDate = {
          $gte: startTimestamp,
          $lte: endTimestamp
        };
      } else {
        // Single date mode - use selected date as timestamp
        const startTimestamp = convertDateToTimestamp(selectedDate, true); // Start of day
        const endTimestamp = convertDateToTimestamp(selectedDate, false); // End of day

        searchParams.businessDate = {
          $gte: startTimestamp,
          $lte: endTimestamp
        };
      }

      const json = {
        page: 1,
        limit: dateRangeMode ? 100 : 10, // Increase limit for range mode to get all days
        search: searchParams
      };

      const response = await HitApi(json, searchIncentive);

      if (response?.statusCode === 200 && response?.data?.docs?.length > 0) {
        if (dateRangeMode && response.data.docs.length > 1) {
          // Aggregate multiple days data into one dashboard
          const aggregatedDashboard = aggregateMultipleDaysData(response.data.docs);
          setDashboardData(aggregatedDashboard);
        } else {
          setDashboardData(response.data.docs[0]); // Get the first dashboard record for single day
        }
      } else {
        setDashboardData(null); // Clear dashboard data when no records found
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  // Handle single date change
  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    if (!dateRangeMode) {
      fetchDashboard();
    }
  };

  // Handle date range mode toggle
  const handleDateRangeModeToggle = () => {
    setDateRangeMode(!dateRangeMode);
    // Fetch data after mode change
    setTimeout(() => fetchDashboard(), 100);
  };

  // Handle start date change
  const handleStartDateChange = (event) => {
    const newStartDate = event.target.value;
    setStartDate(newStartDate);
    if (dateRangeMode) {
      fetchDashboard();
    }
  };

  // Handle end date change
  const handleEndDateChange = (event) => {
    const newEndDate = event.target.value;
    setEndDate(newEndDate);
    if (dateRangeMode) {
      fetchDashboard();
    }
  };

  // Handle calculate incentive
  const handleCalculateIncentive = async () => {
    const selectedUnit = getSelectedUnit();
    const currentUser = getElevateUser();

    if (!selectedUnit?._id) {
      toast.error('Please select a unit first');
      return;
    }

    if (!currentUser?._id) {
      toast.error('User not found. Please login again.');
      return;
    }

    if (!dateRangeMode && !selectedDate) {
      toast.error('Please select a date');
      return;
    }

    if (dateRangeMode && (!startDate || !endDate)) {
      toast.error('Please select start and end dates');
      return;
    }

    setIsCalculating(true);

    try {
      let payload;

      if (dateRangeMode) {
        // Range mode - calculate for date range
        const startTimestamp = convertDateToTimestamp(startDate, true);
        const endTimestamp = convertDateToTimestamp(endDate, true); // Use start of end date for individual day calculation

        payload = {
          unitId: selectedUnit._id,
          calculatedBy: currentUser._id,
          startDate: startTimestamp,
          endDate: endTimestamp,
          isRange: true
        };

        console.log('Calculating incentives for range:', startDate, 'to', endDate, 'with payload:', payload);
      } else {
        // Single date mode
        const timestamp = convertDateToTimestamp(selectedDate, true);

        payload = {
          unitId: selectedUnit._id,
          calculatedBy: currentUser._id,
          businessDate: timestamp
        };

        console.log('Calculating daily incentives for date:', selectedDate, 'with payload:', payload);
      }

      const response = await HitApi(payload, calculateDailyIncentive);

      if (response?.statusCode === 200 || response?.success) {
        const successMessage = dateRangeMode
          ? `Incentives calculated successfully for ${startDate} to ${endDate}!`
          : `Daily incentives calculated successfully for ${selectedDate}!`;
        toast.success(successMessage);

        // Refresh dashboard data after successful calculation
        fetchDashboard();
      } else {
        toast.error(response?.message || 'Failed to calculate daily incentives');
      }
    } catch (error) {
      console.error('Error calculating daily incentives:', error);
      toast.error('Error calculating daily incentives. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Helper functions
  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString('en-IN') || 0}`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTargetColor = (percentage) => {
    if (percentage >= 100) return 'text-green-600 bg-green-100';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Handle settings modal
  const handleSettingsClick = () => {
    setIsSettingsModalOpen(true);
  };

  const handleSettingsModalClose = () => {
    setIsSettingsModalOpen(false);
    // Refresh dashboard data when settings modal is closed
    fetchDashboard();
  };

  if (isLoading) {
    return (
      <div className="p-5">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="p-5">
        <div className="flex justify-between items-start mb-6">
          <PageHeader
            title="Incentive Dashboard"
            description="Overview of staff incentives and performance metrics"
          />

          <div className="flex items-center space-x-3">
            {/* Calculate Incentive Button */}
            <button
              onClick={handleCalculateIncentive}
              disabled={isCalculating}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg shadow-sm transition-colors duration-200 text-sm font-medium ${isCalculating
                  ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
            >
              <Calculator className="h-4 w-4" />
              <span>{isCalculating ? 'Calculating...' : 'Calculate Incentive'}</span>
            </button>

            {/* Date Filter - Toggle between single date and range */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDateRangeModeToggle}
                className={`px-3 py-2 text-xs font-medium rounded-md transition-colors ${dateRangeMode
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                  }`}
              >
                {dateRangeMode ? 'Range Mode' : 'Single Date'}
              </button>

              {dateRangeMode ? (
                <div className="flex items-center space-x-2 bg-white rounded-lg border p-3 shadow-sm">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className="text-sm border-none outline-none focus:ring-0 bg-transparent"
                    placeholder="Start Date"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className="text-sm border-none outline-none focus:ring-0 bg-transparent"
                    placeholder="End Date"
                  />
                </div>
              ) : (
                <div className="flex items-center space-x-2 bg-white rounded-lg border p-3 shadow-sm">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="text-sm border-none outline-none focus:ring-0 bg-transparent"
                  />
                </div>
              )}
            </div>

            {/* Settings Icon */}
            <Settings
              className="h-6 w-6 text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
              onClick={handleSettingsClick}
            />
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">No dashboard data available for selected date</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex justify-between items-start mb-6">
        <PageHeader
          title="Incentive Dashboard"
          description={
            dateRangeMode && startDate && endDate
              ? `Aggregated data from ${new Date(startDate).toLocaleDateString()} to ${new Date(endDate).toLocaleDateString()} (${getSelectedDaysCount()} days)`
              : "Overview of staff incentives and performance metrics"
          }
        />

        <div className="flex items-center space-x-3">
          {/* Calculate Incentive Button */}
          <button
            onClick={handleCalculateIncentive}
            disabled={isCalculating}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg shadow-sm transition-colors duration-200 text-sm font-medium ${isCalculating
                ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            <Calculator className="h-4 w-4" />
            <span>{isCalculating ? 'Calculating...' : 'Calculate Incentive'}</span>
          </button>

          {/* Date Filter - Toggle between single date and range */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDateRangeModeToggle}
              className={`px-3 py-2 text-xs font-medium rounded-md transition-colors ${dateRangeMode
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
            >
              {dateRangeMode ? 'Range Mode' : 'Single Date'}
            </button>

            {dateRangeMode ? (
              <div className="flex items-center space-x-2 bg-white rounded-lg border p-3 shadow-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <input
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  className="text-sm border-none outline-none focus:ring-0 bg-transparent"
                  placeholder="Start Date"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  className="text-sm border-none outline-none focus:ring-0 bg-transparent"
                  placeholder="End Date"
                />
              </div>
            ) : (
              <div className="flex items-center space-x-2 bg-white rounded-lg border p-3 shadow-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="text-sm border-none outline-none focus:ring-0 bg-transparent"
                />
              </div>
            )}
          </div>

          {/* Settings Icon */}
          <Settings
            className="h-6 w-6 text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
            onClick={handleSettingsClick}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Payable Incentive</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(
                  dashboardData.staffMembers?.reduce((sum, staff) =>
                    sum + (staff.incentiveEligible ? staff.totalIncentiveAmount : 0), 0
                  ) || 0
                )}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Total: {formatCurrency(dashboardData.totalIncentiveAmount)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Staff</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.staffCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Bills</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.billCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Services</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData.serviceCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Incentive Payment Breakdown */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-green-50 to-red-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">💰 Incentive Payment Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
              <div className="text-sm text-gray-600 mb-1">✓ Will Be Paid</div>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(
                  dashboardData.staffMembers?.reduce((sum, staff) =>
                    sum + (staff.incentiveEligible ? staff.totalIncentiveAmount : 0), 0
                  ) || 0
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {dashboardData.staffMembers?.filter(staff => staff.incentiveEligible).length || 0} staff eligible
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
              <div className="text-sm text-gray-600 mb-1">✗ Not Payable</div>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(
                  dashboardData.staffMembers?.reduce((sum, staff) =>
                    sum + (staff.incentiveEligible ? 0 : staff.totalIncentiveAmount), 0
                  ) || 0
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {dashboardData.staffMembers?.filter(staff => !staff.incentiveEligible).length || 0} staff not eligible
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 border-l-4 border-gray-400">
              <div className="text-sm text-gray-600 mb-1">Total Calculated</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(dashboardData.totalIncentiveAmount)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {dashboardData.staffCount} total staff
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Aggregated Data Indicator */}
      {dateRangeMode && startDate && endDate && (
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800">
                  Showing aggregated data from multiple days
                </p>
                <p className="text-sm text-blue-600">
                  Selected range: {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                  ({getSelectedDaysCount()} days) - Daily targets have been multiplied by {getSelectedDaysCount()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Target Achievement Overview */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Target Achievement</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm font-medium">Target Achieved</span>
              </div>
              <span className="text-lg font-bold text-green-600">
                {dashboardData.staffWithTargetAchieved}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-sm font-medium">Target Not Achieved</span>
              </div>
              <span className="text-lg font-bold text-red-600">
                {dashboardData.staffWithoutTargetAchieved}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Performance Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Staff Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Salary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {dateRangeMode && startDate && endDate
                    ? `Target (${getSelectedDaysCount()} days)`
                    : 'Daily Target'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Incentive (Payable)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dashboardData.staffMembers?.map((staff, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {console.log("staff----", staff)
                  }
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {staff.staffName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {staff.staffId.phoneNumber}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(staff.salary)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {dateRangeMode && startDate && endDate ?
                      formatCurrency(staff.dailyTarget * getSelectedDaysCount()) :
                      formatCurrency(staff.dailyTarget)
                    }
                    {dateRangeMode && startDate && endDate && getSelectedDaysCount() > 1 && (
                      <div className="text-xs text-gray-500">
                        {formatCurrency(staff.dailyTarget)} × {getSelectedDaysCount()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(staff.totalServiceValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTargetColor(staff.targetPercentage)
                      }`}>
                      {staff.targetPercentage}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {staff.incentiveEligible ? (
                      <>
                        <div className="text-sm font-medium text-green-600">
                          {formatCurrency(staff.totalIncentiveAmount)}
                        </div>
                        <div className="text-xs text-green-600 font-semibold">✓ Payable</div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-gray-400 line-through">
                          {formatCurrency(staff.totalIncentiveAmount)}
                        </div>
                        <div className="text-xs text-red-600 font-semibold">✗ Not Payable</div>
                        <div className="text-xs text-gray-500">(Target not met)</div>
                      </>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {staff.serviceCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Settings Modal */}
      {isSettingsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={handleSettingsModalClose}
            ></div>

            {/* Modal content */}
            <div className="inline-block w-full max-w-6xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              {/* Modal header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Incentive Settings</h2>
                <button
                  onClick={handleSettingsModalClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal body */}
              <div className="max-h-[80vh] overflow-y-auto">
                <IncentiveSetting />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default IncentiveMaster;